import { uniqBy } from "lodash-es";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

export type FilterFactor =
  | "tagSearch"
  | "tickerSearch"
  // ❌ REMOVED: "visibility" (v0.66.0 - all memos are private)
  | "contentSearch"
  | "displayTime"
  | "pinned"
  | "property.hasLink"
  | "property.hasTaskList"
  | "property.hasCode";

export interface JournalFilter {
  factor: FilterFactor;
  value: string;
}

export const getJournalFilterKey = (filter: JournalFilter): string => `${filter.factor}:${filter.value}`;

export const parseFilterQuery = (query: string | null): JournalFilter[] => {
  if (!query) return [];
  try {
    return query.split(",").map((filterStr) => {
      const [factor, value] = filterStr.split(":");
      return {
        factor: factor as FilterFactor,
        value: decodeURIComponent(value || ""),
      };
    });
  } catch {
    return [];
  }
};

export const stringifyFilters = (filters: JournalFilter[]): string => {
  return filters.map((filter) => `${filter.factor}:${encodeURIComponent(filter.value)}`).join(",");
};

interface JournalFilterContextValue {
  filters: JournalFilter[];
  shortcut: string | undefined;
  hasActiveFilters: boolean;
  getFiltersByFactor: (factor: FilterFactor) => JournalFilter[];
  setFilters: (filters: JournalFilter[]) => void;
  addFilter: (filter: JournalFilter) => void;
  removeFilter: (predicate: (f: JournalFilter) => boolean) => void;
  removeFiltersByFactor: (factor: FilterFactor) => void;
  clearAllFilters: () => void;
  setShortcut: (shortcut?: string) => void;
  hasFilter: (filter: JournalFilter) => boolean;
}

const JournalFilterContext = createContext<JournalFilterContextValue | null>(null);

export function JournalFilterProvider({ children }: { children: ReactNode }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const lastSyncedUrlRef = useRef("");
  const lastSyncedStoreRef = useRef("");

  // Initialize from URL
  const [filters, setFiltersState] = useState<JournalFilter[]>(() => {
    return parseFilterQuery(searchParams.get("filter"));
  });
  const [shortcut, setShortcutState] = useState<string | undefined>(undefined);

  // Sync URL to state when URL changes externally
  useEffect(() => {
    const filterParam = searchParams.get("filter") || "";
    if (filterParam !== lastSyncedUrlRef.current) {
      lastSyncedUrlRef.current = filterParam;
      const newFilters = parseFilterQuery(filterParam);
      setFiltersState(newFilters);
      lastSyncedStoreRef.current = stringifyFilters(newFilters);
    }
  }, [searchParams]);

  // Sync state to URL when state changes
  useEffect(() => {
    const storeString = stringifyFilters(filters);
    if (storeString !== lastSyncedStoreRef.current && storeString !== lastSyncedUrlRef.current) {
      lastSyncedStoreRef.current = storeString;
      const newParams = new URLSearchParams(searchParams);
      if (filters.length > 0) {
        newParams.set("filter", storeString);
      } else {
        newParams.delete("filter");
      }
      setSearchParams(newParams, { replace: true });
      lastSyncedUrlRef.current = filters.length > 0 ? storeString : "";
    }
  }, [filters, searchParams, setSearchParams]);

  const getFiltersByFactor = useCallback((factor: FilterFactor) => filters.filter((f) => f.factor === factor), [filters]);

  const setFilters = useCallback((newFilters: JournalFilter[]) => {
    setFiltersState(newFilters);
  }, []);

  const addFilter = useCallback((filter: JournalFilter) => {
    setFiltersState((prev) => uniqBy([...prev, filter], getJournalFilterKey));
  }, []);

  const removeFilter = useCallback((predicate: (f: JournalFilter) => boolean) => {
    setFiltersState((prev) => prev.filter((f) => !predicate(f)));
  }, []);

  const removeFiltersByFactor = useCallback((factor: FilterFactor) => {
    setFiltersState((prev) => prev.filter((f) => f.factor !== factor));
  }, []);

  const clearAllFilters = useCallback(() => {
    setFiltersState([]);
    setShortcutState(undefined);
  }, []);

  const setShortcut = useCallback((newShortcut?: string) => {
    setShortcutState(newShortcut);
  }, []);

  const hasFilter = useCallback(
    (filter: JournalFilter) => filters.some((f) => getJournalFilterKey(f) === getJournalFilterKey(filter)),
    [filters],
  );

  const hasActiveFilters = filters.length > 0 || shortcut !== undefined;

  return (
    <JournalFilterContext.Provider
      value={{
        filters,
        shortcut,
        hasActiveFilters,
        getFiltersByFactor,
        setFilters,
        addFilter,
        removeFilter,
        removeFiltersByFactor,
        clearAllFilters,
        setShortcut,
        hasFilter,
      }}
    >
      {children}
    </JournalFilterContext.Provider>
  );
}

export function useJournalFilterContext() {
  const context = useContext(JournalFilterContext);
  if (!context) {
    throw new Error("useJournalFilterContext must be used within JournalFilterProvider");
  }
  return context;
}

// Alias for backwards compatibility during migration
export const useJournalFilter = useJournalFilterContext;
